/**
 * Page SeanceDetail — vue lecture d'une coaching_session.
 * Route : /dashboard/seances/:id
 *
 * Lecture seule : les seances ne s'editent pas. Les coachs partent
 * d'un modele ou dupliquent une seance existante.
 */
import { useState, useEffect } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft, Copy, Trash2, MoreVertical,
  Calendar, Users as UsersIcon, AlertCircle, Loader2, ClipboardList,
} from 'lucide-react'
import DashboardLayout from '../components/DashboardLayout'
import ProcedeCard from '../components/ProcedeCard'
import { useAuth } from '../context/AuthContext'
import {
  useSession, useDeleteSession, useDuplicateSession,
} from '../hooks/useCoachingSessions'
import { T } from '../theme'

const STATUS_LABELS = {
  draft:     { label: 'Brouillon', color: T.muted2 },
  planned:   { label: 'Planifiée', color: T.blue },
  completed: { label: 'Terminée',  color: T.green },
}

const CONTEXT_LABELS = {
  standard:        'Standard',
  manque_joueurs:  'Manque de joueurs',
  veille_match:    'Veille de match',
  post_match:      'Lendemain de match',
  reprise:         'Reprise',
}

export default function SeanceDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { data: session, loading, error, refetch } = useSession(id)
  const { mutate: doDuplicate, loading: dupLoading } = useDuplicateSession()
  const { mutate: doDelete, loading: delLoading } = useDeleteSession()
  const [menuOpen, setMenuOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768)

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  if (loading) return (
    <DashboardLayout>
      <Center><Loader2 size={28} color={T.gold} className="spin" /></Center>
    </DashboardLayout>
  )
  if (error || !session) return (
    <DashboardLayout>
      <Center>
        <AlertCircle size={32} color={T.red} />
        <p style={{ fontFamily: T.mono, fontSize: 12, color: T.ink, margin: '12px 0' }}>
          {error || 'Séance introuvable.'}
        </p>
        <button onClick={() => refetch()} style={btnGhostStyle}>Réessayer</button>
      </Center>
    </DashboardLayout>
  )

  const isOwner = session.user_id === user?.id
  const isSystemTemplate = session.is_template && session.user_id === null
  const canDelete = isOwner && !isSystemTemplate

  const handleDuplicate = async () => {
    try {
      const copy = await doDuplicate(session.id)
      navigate(`/dashboard/seances/${copy.id}`)
    } catch {
      alert('Échec de la duplication.')
    }
  }
  const handleDelete = async () => {
    if (!confirm(`Supprimer définitivement "${session.name}" ?`)) return
    try {
      await doDelete(session.id)
      navigate('/dashboard/seances?tab=mes-seances')
    } catch (e) {
      alert(e?.message || 'Échec de la suppression.')
    }
  }

  const status = STATUS_LABELS[session.status] || STATUS_LABELS.draft
  const ctxLabel = CONTEXT_LABELS[session.context] || session.context || ''
  const dateStr = session.date
    ? new Date(session.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
    : null

  // Tri des blocks par position
  const blocks = (session.blocks || []).slice().sort((a, b) => a.position - b.position)

  return (
    <DashboardLayout>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } } .spin { animation: spin 1s linear infinite; }`}</style>

      <div style={{ padding: '20px 24px 60px', maxWidth: 1280, margin: '0 auto' }}>
        {/* Top bar */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: 16, gap: 8, flexWrap: 'wrap',
        }}>
          <Link
            to="/dashboard/seances?tab=mes-seances"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              color: T.ink, textDecoration: 'none',
              fontFamily: T.mono, fontSize: 11, letterSpacing: '.12em',
              textTransform: 'uppercase', fontWeight: 700,
            }}
          >
            <ArrowLeft size={13} /> Séances
          </Link>

          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                onClick={() => setMenuOpen(v => !v)}
                style={btnGhostStyle}
                aria-label="Actions"
              >
                <MoreVertical size={14} />
              </button>
              {menuOpen && (
                <DropMenu
                  items={[
                    {
                      icon: <Copy size={12} />, label: 'Dupliquer',
                      onClick: handleDuplicate, disabled: dupLoading,
                    },
                    ...(canDelete ? [
                      { divider: true },
                      {
                        icon: <Trash2 size={12} />, label: 'Supprimer', danger: true,
                        onClick: handleDelete, disabled: delLoading,
                      },
                    ] : []),
                  ]}
                  onClose={() => setMenuOpen(false)}
                />
              )}
            </div>
          </div>
        </div>

        {/* Header titre */}
        <div style={{ marginBottom: 28 }}>
          <div style={{
            fontFamily: T.mono, fontSize: 10, letterSpacing: '.18em',
            textTransform: 'uppercase', color: T.gold, fontWeight: 700, marginBottom: 6,
            display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          }}>
            <span>— Séance{ctxLabel ? ` · ${ctxLabel}` : ''}</span>
            {isSystemTemplate && (
              <span style={{
                padding: '2px 7px', background: T.dark, color: T.gold,
                fontFamily: T.mono, fontSize: 8, letterSpacing: '.18em',
                fontWeight: 700,
              }}>Template Insightball</span>
            )}
            {!isSystemTemplate && session.is_template && (
              <span style={{
                padding: '2px 7px', background: T.bgAlt, color: T.dark,
                border: `1.5px solid ${T.dark}`,
                fontFamily: T.mono, fontSize: 8, letterSpacing: '.18em',
                fontWeight: 700,
              }}>Mon modèle</span>
            )}
          </div>
          <h1 style={{
            fontFamily: T.display, fontSize: 38, color: T.ink,
            textTransform: 'uppercase', letterSpacing: '.01em',
            lineHeight: 1, margin: 0,
          }}>
            {session.name}
          </h1>
          <div style={{
            display: 'flex', gap: 12, marginTop: 10, flexWrap: 'wrap',
            alignItems: 'center',
          }}>
            {dateStr && (
              <span style={metaSpan}>
                <Calendar size={12} /> {dateStr}
              </span>
            )}
            {session.total_duration_minutes && (
              <span style={metaSpan}>
                {session.total_duration_minutes} min
              </span>
            )}
            <span style={metaSpan}>
              {blocks.length} procédé{blocks.length > 1 ? 's' : ''}
            </span>
            {session.number_players_expected && (
              <span style={metaSpan}>
                <UsersIcon size={12} /> {session.number_players_expected} joueurs
              </span>
            )}
            <span style={{
              fontFamily: T.mono, fontSize: 9, fontWeight: 700,
              padding: '2px 8px',
              background: status.color, color: '#fff',
              border: `1px solid ${T.dark}`,
              letterSpacing: '.14em', textTransform: 'uppercase',
            }}>
              {status.label}
            </span>
          </div>
          {session.template_description && (
            <p style={{
              fontFamily: T.mono, fontSize: 12, color: T.ink2,
              marginTop: 14, lineHeight: 1.5,
              maxWidth: isMobile ? '100%' : 720,
              overflowWrap: 'break-word', wordBreak: 'normal',
            }}>
              {session.template_description}
            </p>
          )}
          {session.general_objectives && (
            <p style={{
              fontFamily: T.mono, fontSize: 12, color: T.ink2,
              marginTop: 14, lineHeight: 1.5,
              maxWidth: isMobile ? '100%' : 720,
              overflowWrap: 'break-word', wordBreak: 'normal',
              padding: '10px 14px', background: T.bgAlt,
              border: `1.5px solid ${T.rule}`,
            }}>
              <strong style={{ display: 'block', marginBottom: 4, color: T.ink }}>
                Objectifs généraux
              </strong>
              {session.general_objectives}
            </p>
          )}
        </div>

        {/* Liste des procedes */}
        {blocks.length === 0 ? (
          <div style={{
            padding: 40, textAlign: 'center',
            background: T.surface, border: `2px dashed ${T.rule}`,
          }}>
            <ClipboardList size={32} color={T.muted} style={{ margin: '0 auto 10px', display: 'block' }} />
            <p style={{ fontFamily: T.mono, fontSize: 12, color: T.muted2 }}>
              Cette séance ne contient aucun procédé.
            </p>
          </div>
        ) : (
          <div style={{
            display: 'grid', gap: 24,
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(320px, 1fr))',
          }}>
            {blocks.map(block => (
              <ProcedeCard
                key={block.id}
                data={block}
                context="seance"
                position={block.position}
              />
            ))}
          </div>
        )}

        {/* Notes generales bas de page */}
        {session.notes && (
          <div style={{
            marginTop: 28,
            background: T.surface, border: `2px solid ${T.dark}`,
            boxShadow: `4px 4px 0 ${T.dark}`,
            padding: '16px 18px',
          }}>
            <h2 style={{
              fontFamily: T.display, fontSize: 16, color: T.ink,
              textTransform: 'uppercase', letterSpacing: '.04em',
              margin: '0 0 10px', paddingBottom: 6, borderBottom: `2px solid ${T.dark}`,
            }}>
              Notes
            </h2>
            <p style={{
              fontFamily: T.mono, fontSize: 12, color: T.ink2,
              lineHeight: 1.6, margin: 0, whiteSpace: 'pre-wrap',
            }}>
              {session.notes}
            </p>
          </div>
        )}
      </div>
    </DashboardLayout>
  )
}


// ─── Sous-composants ─────────────────────────────────────────────

const btnGhostStyle = {
  background: T.surface, color: T.ink, border: `2px solid ${T.dark}`,
  padding: '8px 12px', cursor: 'pointer',
  fontFamily: T.mono, fontSize: 11, fontWeight: 700,
  letterSpacing: '.12em', textTransform: 'uppercase',
  display: 'inline-flex', alignItems: 'center', gap: 6,
}

const metaSpan = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  fontFamily: T.mono, fontSize: 11, color: T.muted2,
  letterSpacing: '.04em',
}


function Center({ children }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', padding: 60, textAlign: 'center', gap: 8,
    }}>{children}</div>
  )
}


function DropMenu({ items, onClose }) {
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 30 }} />
      <div style={{
        position: 'absolute', right: 0, top: '110%', zIndex: 31,
        background: T.surface, border: `2px solid ${T.dark}`,
        boxShadow: `4px 4px 0 ${T.dark}`, minWidth: 180,
      }}>
        {items.map((it, i) => it.divider ? (
          <div key={i} style={{ height: 1, background: T.rule }} />
        ) : (
          <button
            key={i}
            type="button"
            disabled={it.disabled}
            onClick={() => { it.onClick(); onClose() }}
            style={{
              width: '100%', padding: '8px 12px', textAlign: 'left',
              background: 'transparent', border: 'none',
              cursor: it.disabled ? 'not-allowed' : 'pointer',
              opacity: it.disabled ? 0.5 : 1,
              display: 'flex', alignItems: 'center', gap: 8,
              fontFamily: T.mono, fontSize: 11,
              color: it.danger ? T.red : T.ink,
            }}
          >
            {it.icon} {it.label}
          </button>
        ))}
      </div>
    </>
  )
}
